import { useEffect, useRef, useState } from "react";
import { track } from "@/lib/analytics";
import { useTurnstile } from "@/lib/turnstile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function sessionId() {
  let id = localStorage.getItem("hudson_session");
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem("hudson_session", id);
  }
  return id;
}

const GREETING = {
  role: "assistant",
  content: "Hi, I'm Hudson — the AI member of the Blissbranding team. Ask me anything about our services, pricing, or client work, and I'll point you the right way.",
};

export const HudsonChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [avail, setAvail] = useState(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", note: "" });
  const [hp, setHp] = useState("");
  const leadOpenedAt = useRef(0);
  const turnstile = useTurnstile("hudson-lead");
  const [leadState, setLeadState] = useState("idle"); // idle | sending | done | error
  const [nudge, setNudge] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (sessionStorage.getItem("hudson_nudged")) return;
    const t = setTimeout(() => {
      sessionStorage.setItem("hudson_nudged", "1");
      setNudge(true);
    }, 5000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open) setNudge(false);
  }, [open]);

  useEffect(() => {
    if (open && avail === null) {
      fetch(`${API}/hudson/availability`).then((r) => r.json()).then(setAvail).catch(() => setAvail({ live: false }));
    }
  }, [open, avail]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, leadOpen, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/hudson/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId(), message: text }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const ev = JSON.parse(line.slice(5));
          if (ev.d) {
            setMessages((m) => {
              const copy = m.slice();
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + ev.d };
              return copy;
            });
          } else if (ev.error) {
            setMessages((m) => {
              const copy = m.slice();
              copy[copy.length - 1] = { role: "assistant", content: ev.error };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = m.slice();
        copy[copy.length - 1] = { role: "assistant", content: "Hudson hit a snag — please try again in a moment." };
        return copy;
      });
    }
    setBusy(false);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (leadState === "sending") return;
    setLeadState("sending");
    try {
      const res = await fetch(`${API}/hudson/lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId(), ...lead, company_website: hp,
          form_ms: leadOpenedAt.current ? Date.now() - leadOpenedAt.current : null,
          turnstile_token: turnstile.getToken(),
        }),
      });
      if (!res.ok) throw new Error("bad");
      const data = await res.json();
      if (data.lead) track("generate_lead", { lead_source: "hudson" });
      setLeadState("done");
    } catch {
      turnstile.reset();
      setLeadState("error");
    }
  };

  return (
    <>
      {open && (
        <div className="hud-panel" data-testid="hudson-panel">
          <div className="hud-head">
            <img src="/assets/team/hudson.jpg" alt="Hudson" />
            <div>
              <p className="hud-name">Hudson</p>
              <p className="hud-sub">Blissbranding's AI teammate{avail?.live ? " · Samantha is available now" : ""}</p>
            </div>
            <button className="hud-close" onClick={() => setOpen(false)} aria-label="Close chat" data-testid="hudson-close">×</button>
          </div>

          <div className="hud-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`hud-msg hud-${m.role}`}>{m.content || "…"}</div>
            ))}

            {leadOpen && (
              <div className="hud-lead">
                {leadState === "done" ? (
                  <p className="hud-lead-done" data-testid="hudson-lead-done">
                    Got it — the team has your details and will follow up by email. Thanks!
                  </p>
                ) : (
                  <form onSubmit={submitLead}>
                    <p className="hud-lead-title">Leave your details and the team will follow up.</p>
                    <input required placeholder="Your name" value={lead.name} data-testid="hudson-lead-name"
                      onChange={(e) => setLead({ ...lead, name: e.target.value })} />
                    <input required type="email" placeholder="Email address" value={lead.email} data-testid="hudson-lead-email"
                      onChange={(e) => setLead({ ...lead, email: e.target.value })} />
                    <textarea rows="2" placeholder="What do you need? (optional)" value={lead.note} data-testid="hudson-lead-note"
                      onChange={(e) => setLead({ ...lead, note: e.target.value })} />
                    <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="bliss-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                    <div ref={turnstile.containerRef} aria-label="Security verification" />
                    <button type="submit" disabled={leadState === "sending"} data-testid="hudson-lead-submit">
                      {leadState === "sending" ? "Sending…" : "Send to the team"}
                    </button>
                    {leadState === "error" && <p className="hud-lead-err">That didn't go through — check your email and try again.</p>}
                  </form>
                )}
              </div>
            )}
          </div>

          <div className="hud-actions">
            <button className="hud-chip" onClick={() => { leadOpenedAt.current = leadOpenedAt.current || Date.now(); setLeadOpen((v) => !v); }} data-testid="hudson-lead-toggle">
              Leave your details
            </button>
            {avail?.live && (
              <>
                <a className="hud-chip hud-chip-wa" href={avail.whatsapp_link} target="_blank" rel="noreferrer" data-testid="hudson-whatsapp">
                  WhatsApp {avail.whatsapp_display}
                </a>
                <a className="hud-chip" href={`mailto:${avail.email}`} data-testid="hudson-email">Email Samantha</a>
              </>
            )}
          </div>

          <form className="hud-input" onSubmit={(e) => { e.preventDefault(); send(); }}>
            <input value={input} placeholder="Ask Hudson anything…" data-testid="hudson-input"
              onChange={(e) => setInput(e.target.value)} />
            <button type="submit" disabled={busy || !input.trim()} data-testid="hudson-send">
              {busy ? "…" : "Send"}
            </button>
          </form>
        </div>
      )}

      {nudge && !open && (
        <div className="hud-nudge" data-testid="hudson-nudge" onClick={() => setOpen(true)} role="button" tabIndex={0}>
          <span>Hi, I'm Hudson — ask me anything about services or pricing.</span>
          <button className="hud-nudge-x" aria-label="Dismiss" data-testid="hudson-nudge-dismiss"
            onClick={(e) => { e.stopPropagation(); setNudge(false); }}>×</button>
        </div>
      )}

      <button className="hud-launcher" onClick={() => setOpen((v) => !v)} aria-label="Chat with Hudson" data-testid="hudson-launcher">
        <img src="/assets/team/hudson.jpg" alt="" />
        <span className="hud-dot" />
      </button>
    </>
  );
};
